import { toast } from 'react-toastify'
import { snapshot } from 'valtio'
import { type AxiosError } from 'axios'
import { type RecurringPayment, recurringPaymentProxy } from '../../../../../../../shared/proxies/recurringPaymentProxy'
import { buildAxiosInstance } from '../../../../../../../shared/utils/apiUtils'
import { convertFromCentValue } from '../../../../../../../shared/utils/formattingUtils'
import { type RpOccurrence, rpOccurrenceProxy } from '../../rpOccurrenceProxy'
import { rpOccurrenceEditProxy } from '../RpOccurrenceDetail/CardUpdateSlideover/rpOccurrenceEditProxy'
import {
  RpOccurrenceEditProxyFormFieldNamesEnum
} from '../RpOccurrenceDetail/CardUpdateSlideover/rpOccurrenceEditProxyFormFieldNamesEnum'
import { cardAddProxy } from '../../../../../../../shared/components/CardAddSlideOver/cardAddProxy'
import { PaymentResolveRadioValues } from './PaymentResolveRadioValues'
import { UpdateCardRadioValues } from './UpdateCardRadioValues'

interface Response {
  data: {
    rpOccurrence: RpOccurrence
    recurringPayment: RecurringPayment
  }
  error: string | null
  errorCode: { description: string, merchantAction: string }
}
export async function executeRecurringTransactionRetry (): Promise<void> {
  const { resumeRadioValue, updateCardRadioValue, customerCard, form: { values } } = snapshot(rpOccurrenceEditProxy)
  const { rpOccurrence } = snapshot(rpOccurrenceProxy)
  const { displayConvergeSlideOver, displaySlideOverCAP } = snapshot(cardAddProxy)
  if (displayConvergeSlideOver || displaySlideOverCAP) {
    return
  }

  const shouldResumePayment = resumeRadioValue === PaymentResolveRadioValues.RESUME_PAYMENT ?? null
  const shouldUpdateCard = updateCardRadioValue === UpdateCardRadioValues.UPDATE_CARD

  if (!rpOccurrence) {
    return
  }

  if (shouldResumePayment && !values[RpOccurrenceEditProxyFormFieldNamesEnum.NextPaymentDate]) {
    return
  }

  recurringPaymentProxy.retryApiCall.loading = true
  recurringPaymentProxy.retryApiCall.error = null
  try {
    const response = await buildAxiosInstance().post<Response>('/api/rp_occurrences/retry',
      {
        customerCardId: customerCard?.id,
        nextPaymentDate: values[RpOccurrenceEditProxyFormFieldNamesEnum.NextPaymentDate],
        recurringPaymentId: rpOccurrence?.recurringPaymentId,
        rpOccurrenceId: rpOccurrence.id,
        shouldResumePayment,
        shouldUpdateCard
      })

    if (response.data.error) {
      recurringPaymentProxy.retryApiCall.error = response.data.error
      toast.error('There was an error resolving the payment')
    } else {
      const recurringPayment = response.data?.data?.recurringPayment ?? {}
      const rpOccurrence = response.data?.data?.rpOccurrence ?? {}

      recurringPayment.amountCents = convertFromCentValue(recurringPayment.amountCents, recurringPayment.amountCurrencyType)
      recurringPayment.totalAmountChargedCents = convertFromCentValue(recurringPayment.totalAmountChargedCents, recurringPayment.totalAmountChargedCurrencyType)

      rpOccurrence.amountCents = convertFromCentValue(rpOccurrence.amountCents, rpOccurrence.amountCurrencyType)

      if (rpOccurrence.transactions) {
        rpOccurrence.transactions.forEach(txn => {
          txn.amountCents = convertFromCentValue(txn.amountCents, txn.amountCurrencyType)
          txn.remainingAmountCents = convertFromCentValue(txn.remainingAmountCents, txn.remainingAmountCurrencyType)
        })
      }

      recurringPaymentProxy.recurringPayment = recurringPayment
      rpOccurrenceProxy.rpOccurrence = rpOccurrence
      toast.success('Occurrence successfully resolved')
      rpOccurrenceProxy.displayUpdateCardSlideOver = false
    }
  } catch (e) {
    if (e instanceof Error) {
      recurringPaymentProxy.retryApiCall.error = e.message
      recurringPaymentProxy.retryApiCall.errorMessage = (e as AxiosError<Response>).response?.data.error
      recurringPaymentProxy.retryApiCall.errorCode = (e as AxiosError<Response>).response?.data.errorCode
    }
  } finally {
    recurringPaymentProxy.retryApiCall.loading = false
  }
}
